import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input, type InputProps } from '@/components/ui';
import { cn } from '@/lib/utils';

/**
 * Password field with a reveal toggle.
 *
 * Typing a 12-character password blind on a phone keyboard is where most
 * failed sign-ins come from. The toggle is a button rather than a checkbox so
 * it can sit inside the field, and it is excluded from the tab order — tabbing
 * from the password should reach the submit button, not a decoration.
 */
export function PasswordInput({ className, ...props }: InputProps) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <Input
        {...props}
        type={visible ? 'text' : 'password'}
        className={cn('pr-12', className)}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setVisible((value) => !value)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        className="absolute right-1 top-1/2 flex h-9 w-10 -translate-y-1/2 items-center justify-center rounded-lg text-foreground-secondary transition-colors hover:bg-accent hover:text-foreground"
      >
        {visible ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}
